import i18n from "./i18n";
import seoConfig from "./seo/routes.json";

const DEFAULT_ROBOTS = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const LOCALE_MAP = {
  en: "en_US",
  fr: "fr_FR",
  ja: "ja_JP",
  de: "de_DE",
  es: "es_ES",
  it: "it_IT",
  pt: "pt_PT",
  ru: "ru_RU",
  tr: "tr_TR",
  nl: "nl_NL",
  sv: "sv_SE",
  pl: "pl_PL",
  ar: "ar_AR",
  el: "el_GR",
  cs: "cs_CZ",
  uk: "uk_UA",
  ro: "ro_RO",
  hi: "hi_IN",
};

const routeMetaByName = seoConfig.routes.reduce((accumulator, route) => {
  accumulator[route.name] = route;
  return accumulator;
}, {});

function getLocalizedSite(locale) {
  const overrides = seoConfig.site.localeOverrides || {};
  const langKey = (locale || "").split("-")[0];
  const override = overrides[locale] || overrides[langKey] || {};
  return { ...seoConfig.site, ...override };
}

function getRouteMeta(route) {
  return routeMetaByName[route?.name] || routeMetaByName.Home;
}

function getAbsoluteUrl(path = "/") {
  return new URL(path, `${seoConfig.site.url}/`).toString();
}

let activeSite = seoConfig.site;

function upsertMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function upsertLink(rel, href, { hreflang } = {}) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    if (hreflang) {
      element.setAttribute("hreflang", hreflang);
    }
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
}

function upsertStructuredData(schema) {
  const entries = Array.isArray(schema) ? schema : [schema];
  const existingElements = document.head.querySelectorAll('script#app-schema, script[data-app-schema="true"]');

  existingElements.forEach((element) => element.remove());

  entries.forEach((entry) => {
    const element = document.createElement("script");
    element.setAttribute("type", "application/ld+json");
    element.setAttribute("data-app-schema", "true");
    element.textContent = JSON.stringify(entry, null, 2);
    document.head.appendChild(element);
  });
}

function buildBreadcrumbSchema(routeMeta, canonicalUrl) {
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: activeSite.url,
    },
  ];

  if (routeMeta.path !== "/") {
    items.push({
      "@type": "ListItem",
      position: 2,
      name: routeMeta.breadcrumb,
      item: canonicalUrl,
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

function buildSoftwareApplicationSchema(imageUrl) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: activeSite.name,
    alternateName: activeSite.shortName,
    applicationCategory: "EducationalApplication",
    operatingSystem: "iOS, iPadOS, Android, Web",
    description: activeSite.description,
    url: activeSite.url,
    image: imageUrl,
    isAccessibleForFree: true,
    downloadUrl: activeSite.appStoreUrl,
    publisher: {
      "@type": "Person",
      name: activeSite.authorName,
      email: activeSite.contactEmail,
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
    },
    featureList: seoConfig.features,
    sameAs: [activeSite.appStoreUrl],
  };
}

function buildHomeSchema(imageUrl) {
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: activeSite.name,
      url: activeSite.url,
      description: activeSite.description,
      inLanguage: "en",
    },
    buildSoftwareApplicationSchema(imageUrl),
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: seoConfig.faq.map((entry) => ({
        "@type": "Question",
        name: entry.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: entry.answer,
        },
      })),
    },
  ];
}

function buildPageSchema(routeMeta, canonicalUrl) {
  return {
    "@context": "https://schema.org",
    "@type": routeMeta.pageType || "WebPage",
    name: routeMeta.title,
    url: canonicalUrl,
    description: routeMeta.description,
    dateModified: routeMeta.updatedAt,
    isPartOf: {
      "@type": "WebSite",
      name: activeSite.name,
      url: activeSite.url,
    },
    about: {
      "@type": "SoftwareApplication",
      name: activeSite.name,
    },
    inLanguage: "en",
  };
}

function buildStructuredData(routeMeta) {
  const canonicalUrl = getAbsoluteUrl(routeMeta.path);
  const imageUrl = getAbsoluteUrl(activeSite.image);
  const schema = [buildPageSchema(routeMeta, canonicalUrl), buildBreadcrumbSchema(routeMeta, canonicalUrl)];

  if (routeMeta.path === "/") {
    return buildHomeSchema(imageUrl);
  }

  schema.push(buildSoftwareApplicationSchema(imageUrl));

  return schema;
}

export function applyRouteSeo(route) {
  if (typeof document === "undefined") {
    return;
  }

  const locale = i18n.global.locale?.value || "en";
  activeSite = getLocalizedSite(locale);

  const routeMeta = getRouteMeta(route);
  const canonicalUrl = getAbsoluteUrl(routeMeta.path);
  const imageUrl = getAbsoluteUrl(activeSite.image);
  const ogType = routeMeta.path === "/" ? "website" : "article";

  document.title = routeMeta.title;
  document.documentElement.setAttribute("lang", locale);

  upsertMeta("name", "description", routeMeta.description);
  upsertMeta(
    "name",
    "robots",
    route?.meta?.noIndex ? "noindex, nofollow" : DEFAULT_ROBOTS,
  );
  upsertMeta("name", "theme-color", "#ffffff");
  upsertMeta("name", "application-name", activeSite.shortName);
  upsertMeta("name", "apple-mobile-web-app-title", activeSite.shortName);
  upsertMeta("name", "apple-itunes-app", `app-id=${activeSite.appId}, app-argument=${canonicalUrl}`);
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", routeMeta.title);
  upsertMeta("name", "twitter:description", routeMeta.description);
  upsertMeta("name", "twitter:image", imageUrl);
  upsertMeta("name", "twitter:image:alt", activeSite.imageAlt);

  upsertMeta("property", "og:site_name", activeSite.shortName);
  upsertMeta("property", "og:locale", LOCALE_MAP[locale] || "en_US");
  upsertMeta("property", "og:type", ogType);
  upsertMeta("property", "og:title", routeMeta.title);
  upsertMeta("property", "og:description", routeMeta.description);
  upsertMeta("property", "og:url", canonicalUrl);
  upsertMeta("property", "og:image", imageUrl);
  upsertMeta("property", "og:image:secure_url", imageUrl);
  upsertMeta("property", "og:image:type", "image/png");
  upsertMeta("property", "og:image:width", "1200");
  upsertMeta("property", "og:image:height", "630");
  upsertMeta("property", "og:image:alt", activeSite.imageAlt);
  upsertMeta(
    "property",
    "article:published_time",
    `${routeMeta.publishedAt || routeMeta.updatedAt}T00:00:00+00:00`,
  );
  upsertMeta("property", "article:modified_time", `${routeMeta.updatedAt}T00:00:00+00:00`);

  upsertLink("canonical", canonicalUrl);
  // The site serves one URL per route across all languages (locale is
  // resolved client-side). Emitting per-language hreflang pointing at the
  // same URL trips Search Console's wrong-canonical detector, so we only
  // declare `x-default` and remove any per-language alternates left over
  // from a previous navigation.
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]:not([hreflang="x-default"])')
    .forEach((element) => element.remove());
  upsertLink("alternate", canonicalUrl, { hreflang: "x-default" });

  upsertStructuredData(buildStructuredData(routeMeta));
}

export function getSeoRoutes() {
  return seoConfig.routes;
}
